import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { DomainScanResult, ScanProgress, FilterOptions } from '@/types';

interface ScanState {
  results: DomainScanResult[];
  progress: ScanProgress;
  filters: FilterOptions;
  isLoading: boolean;
  error: string | null;
  searchedDomain: string | null;
}

type ScanAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_RESULTS'; payload: DomainScanResult[] }
  | { type: 'ADD_RESULT'; payload: DomainScanResult }
  | { type: 'ADD_RESULTS'; payload: DomainScanResult[] }
  | { type: 'UPDATE_RESULT'; payload: DomainScanResult }
  | { type: 'DELETE_RESULT'; payload: string }
  | { type: 'SET_PROGRESS'; payload: number | Partial<ScanProgress> }
  | { type: 'SET_FILTERS'; payload: FilterOptions }
  | { type: 'SET_SEARCHED_DOMAIN'; payload: string | null }
  | { type: 'CLEAR_RESULTS' };

const initialState: ScanState = {
  results: [],
  progress: {
    current: 0,
    total: 0,
    currentDomain: '',
    isScanning: false,
  },
  filters: {
    page: 1,
    limit: 50,
  },
  isLoading: false,
  error: null,
  searchedDomain: null,
};

const scanReducer = (state: ScanState, action: ScanAction): ScanState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_RESULTS':
      return { ...state, results: action.payload, isLoading: false, error: null };

    case 'ADD_RESULT':
      return { ...state, results: [action.payload, ...state.results] };

    case 'ADD_RESULTS':
      return { ...state, results: [...action.payload, ...state.results] };

    case 'UPDATE_RESULT':
      return {
        ...state,
        results: state.results.map(result =>
          result.id === action.payload.id ? action.payload : result
        ),
      };

    case 'DELETE_RESULT':
      return {
        ...state,
        results: state.results.filter(result => result.id !== action.payload),
      };

    case 'SET_PROGRESS':
      return {
        ...state,
        progress: typeof action.payload === 'number'
          ? { ...state.progress, current: action.payload, total: 100, isScanning: action.payload > 0 && action.payload < 100 }
          : { ...state.progress, ...action.payload },
      };

    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case 'SET_SEARCHED_DOMAIN':
      return { ...state, searchedDomain: action.payload };

    case 'CLEAR_RESULTS':
      return { ...state, results: [], progress: initialState.progress, searchedDomain: null };

    default:
      return state;
  }
};

interface ScanContextType {
  state: ScanState;
  dispatch: React.Dispatch<ScanAction>;
}

const ScanContext = createContext<ScanContextType | undefined>(undefined);

export const ScanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(scanReducer, initialState);

  return (
    <ScanContext.Provider value={{ state, dispatch }}>
      {children}
    </ScanContext.Provider>
  );
};

export const useScan = () => {
  const context = useContext(ScanContext);
  if (context === undefined) {
    throw new Error('useScan must be used within a ScanProvider');
  }
  return context;
};
